import { useEffect } from 'react';
import { Navigate, Outlet, useLocation } from 'react-router-dom';

import { Transition } from '@headlessui/react';

import { useAuth } from '../contexts/AuthContext';
import { FeatureFlagProvider } from '../contexts/FeatureFlagContext';

import { defaultTransitionProps } from '../v2/lib/animation';

import { GlobalModalsProvider } from '../contexts/GlobalModalsContext';
import IsMobileDeviceScreen from '../v2/components/IsMobileDeviceScreen';
import MainHeader from '../v2/components/Headers/MainHeader';
import ImportStatusToast from '../v2/components/ScheduledEvents/ImportStatusToast';
import useIsMobileDevice from '../v2/hooks/useIsMobileDevice';
import classNames from 'classnames';
import { useHomecomingLoader } from '../v2/hooks/useHomecomingLoader';

const UNSUPPORTED_ACCOUNT_PATH = '/unsupported-account';

const HIDE_HEADER_ROUTES = [
  '/billing/updated',
  '/billing/success',
  '/logout',
  '/intro',
  '/stripe-checkout',
  UNSUPPORTED_ACCOUNT_PATH,
];

function AuthedContainerV2() {
  const location = useLocation();

  const {
    auth0AccessToken,
    authedProviderUser,
    setHasInitialLoadError,
    refreshAuthedProviderUser,
    logout,
    showUpgradeBanner,
  } = useAuth();

  const isMobileDevice = useIsMobileDevice();
  const HomecomingLoader = useHomecomingLoader();

  const loadInitialAuthedProviderUser = async () => {
    const response = await refreshAuthedProviderUser();
    if (response.error && response.error.message !== 'Invalid bearer token') {
      // Don't show an error message for invalid token errors.
      setHasInitialLoadError(true);
      await logout();
    }
  };

  useEffect(() => {
    if (auth0AccessToken && !authedProviderUser) {
      // This will occur upon a page refresh.
      loadInitialAuthedProviderUser();
    }
  }, [auth0AccessToken, authedProviderUser]);

  if (!auth0AccessToken) {
    // Redirect them to the /login page, but save the current location they were
    // trying to go to when they were redirected. This allows us to send them
    // along to that page after they login, which is a nicer user experience
    // than dropping them off on the home page.
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  if (!authedProviderUser) {
    return HomecomingLoader;
  }

  // Legacy (V1) provider users: V1 UI was removed. Show a logout-capable
  // interstitial instead of mounting the V2 app (or CatchAll-looping to /).
  // Prefer this over the mobile gate so users can still sign out on phones.
  if (!authedProviderUser.isV2) {
    if (location.pathname !== UNSUPPORTED_ACCOUNT_PATH) {
      return <Navigate to={UNSUPPORTED_ACCOUNT_PATH} replace />;
    }
    return <Outlet />;
  }

  const hideHeader = HIDE_HEADER_ROUTES.includes(location.pathname);

  if (isMobileDevice) {
    return <IsMobileDeviceScreen />;
  }

  return (
    <FeatureFlagProvider>
      <GlobalModalsProvider>
        <Transition
          show
          {...defaultTransitionProps}
          className="relative flex min-h-screen flex-col"
        >
          {!hideHeader && <MainHeader />}

          <main
            className={classNames(
              !hideHeader
                ? showUpgradeBanner
                  ? 'mt-[calc(var(--top-nav-height)+var(--upgrade-banner-height))] print:mt-0'
                  : 'mt-[var(--top-nav-height)] print:mt-0'
                : '',
            )}
          >
            <Outlet />
          </main>

          <ImportStatusToast />
        </Transition>
      </GlobalModalsProvider>
    </FeatureFlagProvider>
  );
}

export default AuthedContainerV2;
